import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, objectSpread2 as _objectSpread2, extends as _extends, defineProperty as _defineProperty } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import { Body, Heading } from '@walmart-web/livingdesign-components';
import classNames from 'classnames';
import React__default, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { applyCommonProps } from '../../utility/commonProps.js';
import { Popover } from '../Popover/Popover.js';
import { generateUniqueId } from './helper.js';
import styles from './InfoUnderline.module.scss.js';

var _excluded = ["variant", "size", "hasUnderline", "bold", "title", "description", "maxWidth"];
/**
 * InfoUnderline component displays text with a dotted underline and shows additional information in a popover on hover/focus.
 * Use this component for headings or standalone text elements that need additional contextual information.
 * @prop variant The variant of the component ('Body' or 'Heading').
 * @prop size (Optional) The size of the component ('small', 'medium', or 'large'). Default is 'medium'. Note: For 'Heading' variant, only 'small' size is available.
 * @prop hasUnderline (Optional) Whether to show the dotted underline. Default is true.
 * @prop bold (Optional) Whether to display the title text in bold. Default is false.
 * @prop title The title text to display with the underline.
 * @prop description (Optional) Additional description text shown in the popover.
 * @prop maxWidth (Optional) Maximum width of the text component. If text is longer than maxWidth, it will be truncated.
 */
var InfoUnderline = function InfoUnderline(props) {
  var _applyCommonProps = applyCommonProps(props, InfoUnderline),
    variant = _applyCommonProps.variant,
    _applyCommonProps$siz = _applyCommonProps.size,
    size = _applyCommonProps$siz === void 0 ? 'medium' : _applyCommonProps$siz,
    _applyCommonProps$has = _applyCommonProps.hasUnderline,
    hasUnderline = _applyCommonProps$has === void 0 ? true : _applyCommonProps$has,
    _applyCommonProps$bol = _applyCommonProps.bold,
    bold = _applyCommonProps$bol === void 0 ? false : _applyCommonProps$bol,
    title = _applyCommonProps.title,
    description = _applyCommonProps.description,
    maxWidth = _applyCommonProps.maxWidth,
    restProps = _objectWithoutProperties(_applyCommonProps, _excluded);
  var contentRef = useRef(null);
  var textSpanRef = useRef(null);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isPopoverOpen = _useState2[0],
    setIsPopoverOpen = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isTextOverflowing = _useState4[0],
    setIsTextOverflowing = _useState4[1];
  var _useState5 = useState(generateUniqueId()),
    _useState6 = _slicedToArray(_useState5, 1),
    uniqueId = _useState6[0];
  var handleMouseEnter = useCallback(function () {
    return setIsPopoverOpen(true);
  }, []);
  var handleMouseLeave = useCallback(function () {
    return setIsPopoverOpen(false);
  }, []);
  var handleFocus = useCallback(function () {
    return setIsPopoverOpen(true);
  }, []);
  var handleBlur = useCallback(function () {
    return setIsPopoverOpen(false);
  }, []);
  var handleKeyDown = useCallback(function (e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsPopoverOpen(function (prev) {
        return !prev;
      });
    }
  }, []);
  var descriptionId = "".concat(uniqueId, "-desc");
  var contentId = "".concat(uniqueId, "-content");
  var titleId = "".concat(uniqueId, "-title");
  var checkOverflow = function checkOverflow() {
    if (textSpanRef.current) {
      setIsTextOverflowing(textSpanRef.current.scrollWidth > textSpanRef.current.clientWidth);
    }
  };
  useEffect(function () {
    checkOverflow();
    var resizeObserver = new ResizeObserver(function () {
      checkOverflow();
    });
    if (textSpanRef.current) {
      resizeObserver.observe(textSpanRef.current);
    }
    return function () {
      resizeObserver.disconnect();
    };
  }, []);
  useEffect(function () {
    var handleKeyDownEvent = function handleKeyDownEvent(e) {
      if (e.key === 'Escape' && isPopoverOpen) {
        setIsPopoverOpen(false);
      }
    };
    if (isPopoverOpen) {
      document.addEventListener('keydown', handleKeyDownEvent);
      return function () {
        document.removeEventListener('keydown', handleKeyDownEvent);
      };
    }
    return undefined;
  }, [isPopoverOpen]);

  // Get the CSS class for the span based on component props
  var getSpanClassName = function getSpanClassName() {
    var _classNames;
    if (!hasUnderline) return styles.innerSpan;
    return classNames(styles.innerSpan, (_classNames = {}, _defineProperty(_classNames, styles.innerSpanBodySmall, variant === 'Body' && size === 'small'), _defineProperty(_classNames, styles.innerSpanBodyMedium, variant === 'Body' && size === 'medium'), _defineProperty(_classNames, styles.innerSpanBodyLarge, variant === 'Body' && size === 'large'), _defineProperty(_classNames, styles.innerSpanHeadingSmall, variant === 'Heading'), _classNames));
  };
  var popoverContent = useMemo(function () {
    return /*#__PURE__*/React__default.createElement("div", {
      className: styles.popoverContent,
      role: "dialog",
      "aria-labelledby": titleId,
      id: contentId
    }, /*#__PURE__*/React__default.createElement("div", {
      id: titleId,
      className: styles.popoverTitle,
      role: "heading",
      "aria-level": 3
    }, title), description && /*#__PURE__*/React__default.createElement("p", {
      id: descriptionId,
      className: styles.popoverDescription
    }, description));
  }, [title, description, contentId, descriptionId, titleId]);
  var spanStyle = _objectSpread2(_objectSpread2({}, maxWidth ? {
    maxWidth: maxWidth
  } : {}), bold ? {
    fontWeight: 'bold'
  } : {
    fontWeight: 'normal'
  });
  var ariaProps = _objectSpread2(_objectSpread2({}, isPopoverOpen && description ? {
    'aria-describedby': descriptionId
  } : {}), isPopoverOpen ? {
    'aria-controls': contentId
  } : {});
  return /*#__PURE__*/React__default.createElement(Popover, {
    isOpen: isPopoverOpen,
    triggerRef: contentRef,
    content: popoverContent,
    position: "bottomCenter",
    onClose: function onClose() {
      return setIsPopoverOpen(false);
    },
    hasNubbin: true
  }, /*#__PURE__*/React__default.createElement("div", {
    ref: contentRef,
    className: styles.wrapper,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onKeyDown: handleKeyDown,
    tabIndex: 0,
    role: "button",
    "aria-haspopup": "dialog",
    "aria-expanded": isPopoverOpen
  }, variant === 'Body' ? /*#__PURE__*/React__default.createElement(Body, _extends({
    size: size,
    UNSAFE_className: styles.infoUnderline
  }, ariaProps, restProps), /*#__PURE__*/React__default.createElement("span", {
    ref: textSpanRef,
    className: getSpanClassName(),
    style: spanStyle,
    title: isTextOverflowing ? title : ''
  }, title)) : /*#__PURE__*/React__default.createElement(Heading, _extends({
    size: size,
    UNSAFE_className: styles.infoUnderline
  }, ariaProps, restProps), /*#__PURE__*/React__default.createElement("span", {
    ref: textSpanRef,
    className: getSpanClassName(),
    style: spanStyle,
    title: isTextOverflowing ? title : undefined
  }, title))));
};
InfoUnderline.displayName = 'InfoUnderline';
var InfoUnderline$1 = /*#__PURE__*/React__default.memo(InfoUnderline);

export { InfoUnderline$1 as default };
